import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Building2,
  UserCheck,
  CheckSquare,
  Mail,
  Phone,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Target,
  FileText,
  Share2,
  Megaphone,
  Lightbulb,
  Image,
  Globe,
  Clock,
  Bell,
  Facebook,
  Activity,
  Wifi,
  Users,
  PhoneMissed,
  ListOrdered,
  FileArchive,
  TrendingUp,
  Instagram,
  Search,
} from 'lucide-react'
import { useAuthStore } from '../../stores/auth'
import { useUIStore } from '../../stores/ui'

interface NavItem {
  to: string
  icon: any
  label: string
  roles: string[]
}

interface NavSection {
  title: string
  items: NavItem[]
}

const ALL_ROLES = ['admin', 'manager', 'sales_rep', 'marketing']
const CRM_ROLES = ['admin', 'manager', 'sales_rep']
const ADMIN_ROLES = ['admin', 'manager']
const CONTENT_ROLES = ['admin', 'manager', 'marketing']
const MGMT_ROLES = ['admin', 'manager']

const sections: NavSection[] = [
  {
    title: '',
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Home', roles: ALL_ROLES },
    ],
  },
  {
    title: 'Sales',
    items: [
      { to: '/queue', icon: ListOrdered, label: 'Queue', roles: CRM_ROLES },
      { to: '/leads', icon: Target, label: 'Leads', roles: CRM_ROLES },
      { to: '/companies', icon: Building2, label: 'Companies', roles: CRM_ROLES },
      { to: '/tasks', icon: CheckSquare, label: 'Tasks', roles: CRM_ROLES },
      { to: '/email', icon: Mail, label: 'Email', roles: CRM_ROLES },
      { to: '/voip', icon: Phone, label: 'Calls', roles: CRM_ROLES },
      { to: '/unknown-callers', icon: PhoneMissed, label: 'Unknown Callers', roles: ADMIN_ROLES },
    ],
  },
  {
    title: 'Tools',
    items: [
      { to: '/tools/blog', icon: FileText, label: 'Blog Writer', roles: CONTENT_ROLES },
      { to: '/tools/social', icon: Share2, label: 'Social Media', roles: CONTENT_ROLES },
      { to: '/tools/campaign', icon: Megaphone, label: 'Campaign', roles: CONTENT_ROLES },
      { to: '/tools/strategy', icon: Lightbulb, label: 'Strategy', roles: CONTENT_ROLES },
      { to: '/tools/image', icon: Image, label: 'Image Gen', roles: CONTENT_ROLES },
      { to: '/tools/scraper', icon: Globe, label: 'Scraper', roles: CONTENT_ROLES },
    ],
  },
  {
    title: 'Insights',
    items: [
      { to: '/reports', icon: BarChart3, label: 'Reports', roles: MGMT_ROLES },
      { to: '/history', icon: Clock, label: 'Run History', roles: CONTENT_ROLES },
      { to: '/meta', icon: Facebook, label: 'Meta Ads', roles: CONTENT_ROLES },
      { to: '/meta/analytics', icon: TrendingUp, label: 'Ad Analytics', roles: MGMT_ROLES },
      { to: '/social-analytics', icon: Instagram, label: 'Social Analytics', roles: CONTENT_ROLES },
      { to: '/ig-discovery', icon: Search, label: 'IG Discovery', roles: ALL_ROLES },
    ],
  },
  {
    title: 'Management',
    items: [
      { to: '/team', icon: Users, label: 'Teams', roles: MGMT_ROLES },
      { to: '/team/workload', icon: UserCheck, label: 'Workload', roles: MGMT_ROLES },
      { to: '/team/analytics', icon: BarChart3, label: 'Team Analytics', roles: MGMT_ROLES },
      { to: '/presence', icon: Wifi, label: 'Team Activity', roles: MGMT_ROLES },
    ],
  },
  {
    title: 'Admin',
    items: [
      { to: '/users', icon: UserCheck, label: 'Users', roles: ['admin'] },
      { to: '/settings', icon: Settings, label: 'Settings', roles: ['admin'] },
      { to: '/data-transfers', icon: FileArchive, label: 'Data Transfers', roles: ['admin', 'manager'] },
      { to: '/tasks-monitor', icon: Activity, label: 'Task Monitor', roles: ['admin'] },
      { to: '/notifications', icon: Bell, label: 'Notifications', roles: ['admin', 'manager', 'marketing', 'sales_rep'] },
    ],
  },
]

export default function Sidebar() {
  const user = useAuthStore((s) => s.user)
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const role = user?.role || 'sales_rep'

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-gray-900 text-white transition-all duration-200 z-30 flex-col hidden md:flex ${
        sidebarOpen ? 'w-56' : 'w-16'
      }`}
    >
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 h-14 border-b border-gray-800">
        <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center font-bold text-sm">
          ON
        </div>
        {sidebarOpen && <span className="font-semibold text-sm">OpsNest</span>}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-2 overflow-y-auto scrollbar-thin">
        {sections.map((section, si) => {
          const filteredItems = section.items.filter((item) => item.roles.includes(role))
          if (filteredItems.length === 0) return null
          return (
            <div key={si} className="mb-1">
              {section.title && sidebarOpen && (
                <p className="px-4 pt-3 pb-1 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{section.title}</p>
              )}
              {!sidebarOpen && section.title && <div className="border-t border-gray-800 mx-3 my-1.5" />}
              {filteredItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800'
                    } ${sidebarOpen ? '' : 'justify-center'}`
                  }
                >
                  <item.icon size={17} />
                  {sidebarOpen && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          )
        })}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="flex items-center justify-center h-10 border-t border-gray-800 text-gray-400 hover:text-white"
      >
        {sidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </button>
    </aside>
  )
}
